import type { Permission, Role } from '@kortex/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';

export interface PropertySummary {
  id: string;
  name: string;
  code: string;
  city?: string;
  role: Role;
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  mustChangePassword?: boolean;
}

export interface Session {
  user: SessionUser;
  property: { id: string; name: string; code?: string; gstin: string; stateCode: string };
  role: Role;
  permissions: Permission[];
  properties: PropertySummary[];
}

interface AuthValue {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchProperty: (propertyId: string) => Promise<void>;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('loading');

  const loadMe = useCallback(async () => {
    const me = await api.get<Session>('/api/auth/me');
    setSession(me);
    setStatus('authenticated');
  }, []);

  // On boot, try the refresh cookie so a reload keeps the user signed in.
  useEffect(() => {
    let cancelled = false;
    setUnauthenticatedHandler(() => {
      setSession(null);
      setStatus('anonymous');
    });

    void (async () => {
      const refreshed = await api.refresh();
      if (cancelled) return;
      if (!refreshed) {
        setStatus('anonymous');
        return;
      }
      try {
        await loadMe();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/login', { email, password });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setAccessToken(null);
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  const switchProperty = useCallback(
    async (propertyId: string) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/switch-property', { propertyId });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const value = useMemo<AuthValue>(
    () => ({
      session,
      status,
      signIn,
      signOut,
      switchProperty,
      can: (...permissions) => permissions.every((p) => session?.permissions.includes(p) ?? false),
      canAny: (...permissions) => permissions.some((p) => session?.permissions.includes(p) ?? false),
    }),
    [session, status, signIn, signOut, switchProperty],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** Renders children only when the signed-in role holds the permission. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission | Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  return <>{can(...list) ? children : fallback}</>;
}
